import React from 'react';
import { useReduxState } from 'hooks';
import { getAllLanguageOptionsByLangCode } from 'lib/courses';
import { DisplayFormattedDate, FlagIcon, User } from 'components/common';
import {
  BoxEmpty,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { Icon } from 'components/common/icons';
import {
  AnswersProgress,
  CompletionStatus,
  CourseProgressStatus,
  ReadingProgress,
  TaskPointsProgress,
  VideoProgress
} from 'components/common/tasks';
import {
  CourseUserDeadline,
  CourseUserMandatory,
  CoursePathDeadlines
} from 'components/courses/common';
import { FollowUsersListActions } from '.';

import type { GetTaskUsersRes } from 'shared-types/responses';

interface Props {
  courseVersionTagsByVersionNumber: GetTaskUsersRes['courseVersionTagsByVersionNumber'];
  users: GetTaskUsersRes['users'];
  updateUsers: () => Promise<void>;
}

const FollowUsersList = ({
  courseVersionTagsByVersionNumber,
  users,
  updateUsers
}: Props) => {
  const {
    messages,
    pageState: { course, view },
    serverConfig,
    theme
  } = useReduxState();

  const availableLanguages = getAllLanguageOptionsByLangCode();

  return (
    <div className="follow-users-list">
      {users?.length ? (
        <BoxOverflowWrapper>
          <BoxTable>
            <BoxTableHeader style={{ whiteSpace: 'nowrap' }}>
              <BoxTableCell sticky={true}>{messages.commonUser}</BoxTableCell>
              {view === 'overview' ? (
                <>
                  <BoxTableCell>{messages.courseLastOpened}</BoxTableCell>
                  <BoxTableCell>{messages.commonMandatory}</BoxTableCell>
                  <BoxTableCell>{messages.courseTargetSchedule}</BoxTableCell>
                  <BoxTableCell>{messages.commonProgress}</BoxTableCell>
                </>
              ) : (
                <>
                  <BoxTableCell>{messages.tasksReading}</BoxTableCell>
                  <BoxTableCell>{messages.tasksVideos}</BoxTableCell>
                  <BoxTableCell>{messages.tasksAnswers}</BoxTableCell>
                  <BoxTableCell>{messages.tasksTaskPoints}</BoxTableCell>
                  <BoxTableCell>{messages.commonCompleted}</BoxTableCell>
                  {Object?.keys(course?.languageVersions)?.length ? (
                    <BoxTableCell>
                      {messages.courseLanguageVersion}
                    </BoxTableCell>
                  ) : null}
                  <BoxTableCell>{messages.commonTag}</BoxTableCell>
                  <BoxTableCell>{messages.excelCourseFirstEntry}</BoxTableCell>
                  <BoxTableCell>{messages.courseLastOpened}</BoxTableCell>
                  <BoxTableCell>
                    {messages.excelCourseNumberOfEntries}
                  </BoxTableCell>
                  <BoxTableCell>{messages.usersLastLogin}</BoxTableCell>
                </>
              )}
              <BoxTableCell sticky={true} stickyAnchor="right"></BoxTableCell>
            </BoxTableHeader>

            {users.map((user) => (
              <BoxTableRow key={user.id}>
                <BoxTableCell sticky={true} stickyBackgroundColor="white">
                  <User
                    enableProfileLink={true}
                    linkAs={`/admin/courses/${course.slug}/follow/users/${user.id}`}
                    linkHref="/admin/courses/[courseSlug]/follow/users/[userId]"
                    style={{ paddingRight: 20, minWidth: 200 }}
                    user={user}
                  />
                </BoxTableCell>

                {view === 'overview' ? (
                  <>
                    <BoxTableCell
                      icon={
                        user.lastOpenedDate ? ['far', 'calendar-alt'] : null
                      }
                      label={messages.courseLastOpened}
                    >
                      {user.lastOpenedDate ? (
                        <DisplayFormattedDate
                          date={user.lastOpenedDate}
                          withTime={serverConfig.analyticsDatesDisplayTime}
                        />
                      ) : (
                        <span
                          style={{
                            color: theme.textColorLighter,
                            fontWeight: 400
                          }}
                        >
                          {messages.commonNever}
                          {user.progressData?.analyticsDisabledForAdmin ? (
                            <span
                              aria-label={messages.tasksIgnoredInfo}
                              data-tooltip={messages.tasksIgnoredInfo}
                              style={{
                                fontSize: '1rem',
                                color: theme.textColorLighter,
                                marginLeft: 5,
                                position: 'relative',
                                top: -1
                              }}
                            >
                              <Icon icon="exclamation-triangle" />
                            </span>
                          ) : null}
                        </span>
                      )}
                    </BoxTableCell>

                    <BoxTableCell label={messages.commonMandatory}>
                      <CourseUserMandatory
                        mandatoryReasons={user.mandatoryReasons}
                      />
                    </BoxTableCell>

                    <BoxTableCell label={messages.courseTargetSchedule}>
                      {user.deadline ? (
                        <CourseUserDeadline
                          deadline={user.deadline}
                          isActive={
                            user.progressData?.completion?.status !== 'success'
                          }
                          isEdited={Boolean(user.courseSettings?.deadlineType)}
                          showIcon={true}
                        />
                      ) : user.relatedPathDeadlines?.length ? (
                        <CoursePathDeadlines
                          courseId={course.id}
                          isActive={
                            user.progressData?.completion?.status !== 'success'
                          }
                          pathDeadlines={user.relatedPathDeadlines}
                          userId={user.id}
                        />
                      ) : null}
                    </BoxTableCell>

                    <BoxTableCell label={messages.commonProgress}>
                      <CourseProgressStatus
                        course={course}
                        id={`course-user-progress-status-${user.id}`}
                        progressData={user.progressData}
                      />
                    </BoxTableCell>
                  </>
                ) : (
                  <>
                    <BoxTableCell label={messages.tasksReading} shrink={true}>
                      <ReadingProgress
                        analyticsDisabledForAdmin={
                          user.progressData.analyticsDisabledForAdmin
                        }
                        discrepancyInCompletion={
                          user.progressData.discrepancyInCompletion
                            ?.readingProgress
                        }
                        readingProgress={user.progressData.readingProgress}
                        readingProgressMaxTime={
                          user.progressData.readingProgressMaxTime
                        }
                      />
                    </BoxTableCell>

                    <BoxTableCell label={messages.tasksVideos} shrink={true}>
                      <VideoProgress
                        analyticsDisabledForAdmin={
                          user.progressData.analyticsDisabledForAdmin
                        }
                        discrepancyInCompletion={
                          user.progressData.discrepancyInCompletion
                            ?.videoProgress
                        }
                        numberOfVideos={user.progressData.numberOfVideos}
                        videoProgress={user.progressData.videoProgress}
                        videosWatched={
                          user.progressData.videoProgressVideosWatched
                        }
                      />
                    </BoxTableCell>

                    <BoxTableCell label={messages.tasksAnswers} shrink={true}>
                      <AnswersProgress
                        numberOfAnswers={user.progressData.taskAnswers}
                        numberOfTasks={user.progressData.numberOfTasks}
                        numberOfUnreviewedTasks={
                          user.progressData.openQuestionAnswersManualUnreviewed
                        }
                      />
                    </BoxTableCell>

                    <BoxTableCell
                      label={messages.tasksTaskPoints}
                      shrink={true}
                    >
                      <TaskPointsProgress
                        discrepancyInCompletion={
                          user.progressData.discrepancyInCompletion
                            ?.taskPointsProgress
                        }
                        numberOfTasks={user.progressData.numberOfTasks}
                        taskMaxPoints={user.progressData.taskMaxPoints}
                        taskPoints={user.progressData.taskPoints}
                        taskPointsProgress={
                          user.progressData.taskPointsProgress
                        }
                      />
                    </BoxTableCell>

                    <BoxTableCell label={messages.commonCompleted}>
                      {user.progressData.completion ? (
                        <CompletionStatus
                          completion={user.progressData.completion}
                          discrepancyInCompletion={Boolean(
                            user.progressData.discrepancyInCompletion
                          )}
                          progressData={user.progressData}
                        />
                      ) : null}
                    </BoxTableCell>

                    {Object?.keys(course?.languageVersions)?.length ? (
                      <BoxTableCell>
                        {user.progressData?.completion
                          ?.courseLanguageVersion ? (
                          <div className="language">
                            <FlagIcon
                              lang={
                                user.progressData.completion
                                  .courseLanguageVersion
                              }
                            />
                            <span>
                              {
                                availableLanguages[
                                  user.progressData.completion
                                    .courseLanguageVersion
                                ]?.capitalizedName
                              }
                            </span>
                          </div>
                        ) : (
                          <div className="language-not-selected">
                            {messages.commonNotSelected}
                          </div>
                        )}
                      </BoxTableCell>
                    ) : null}

                    <BoxTableCell label={messages.commonTag}>
                      {user.progressData.completion?.courseVersion &&
                      courseVersionTagsByVersionNumber?.[
                        `${user.progressData.completion.courseVersion}`
                      ] ? (
                        <span style={{ whiteSpace: 'nowrap' }}>
                          {
                            courseVersionTagsByVersionNumber[
                              `${user.progressData.completion.courseVersion}`
                            ]
                          }
                        </span>
                      ) : null}
                    </BoxTableCell>

                    <BoxTableCell>
                      <DisplayFormattedDate
                        date={user.firstAccessed}
                        inputDateFormat="dd.MM.y HH:mm"
                        withTime={serverConfig.analyticsDatesDisplayTime}
                      />
                    </BoxTableCell>

                    <BoxTableCell>
                      <DisplayFormattedDate
                        date={user.lastOpenedDate}
                        withTime={serverConfig.analyticsDatesDisplayTime}
                      />
                    </BoxTableCell>

                    <BoxTableCell>{user.numberOfEntries}</BoxTableCell>

                    <BoxTableCell>
                      <DisplayFormattedDate date={user.lastLogin} />
                    </BoxTableCell>
                  </>
                )}

                <BoxTableCell
                  sticky={true}
                  stickyAnchor="right"
                  stickyBackgroundColor="white"
                  shrink={true}
                >
                  <FollowUsersListActions
                    user={user}
                    updateUsers={updateUsers}
                  />
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        </BoxOverflowWrapper>
      ) : (
        <BoxEmpty text={messages.tasksNoUsers} />
      )}

      <style jsx>{`
        .language {
          align-items: center;
          display: flex;
          gap: 5px;
        }
        .language-not-selected {
          color: ${theme.textColorLighter};
          font-weight: 400;
        }
      `}</style>
    </div>
  );
};

export default FollowUsersList;
